import { Button } from "@/components/ui/button"
import { useBidLifecycleActions } from "@/components/bid-lifecycle/useBidLifecycleActions"
import {
  LIFECYCLE_ACTION_LABELS,
  bidLifecycleOf,
  type LifecycleAction,
} from "@/lib/bid-lifecycle"
import type { Bid } from "@/lib/schemas"
import { cn } from "@/lib/utils"

/** What the Submit page offers, by lifecycle: the submission steps only. */
const SUBMIT_PAGE_ACTIONS: Record<string, LifecycleAction[]> = {
  active: ["mark_submitted"],
  lodged: ["record_result", "reopen"],
  closed: ["edit_result", "reopen"],
}

/**
 * The lifecycle actions on the Submit page (plan §2.7): Mark as submitted,
 * Record result, Reopen. One row of buttons; the first is the page's primary
 * action. Nothing for a lead, which has not started bidding.
 */
interface BidLifecycleActionsRowProps {
  bid: Bid
  readOnly: boolean
  className?: string
}

export function BidLifecycleActionsRow({ bid, readOnly, className }: BidLifecycleActionsRowProps) {
  const actions = useBidLifecycleActions(bid)
  if (readOnly) return null
  const offered = SUBMIT_PAGE_ACTIONS[bidLifecycleOf(bid).lifecycle] ?? []
  if (offered.length === 0) return null

  return (
    <div className={cn("flex items-center gap-2", className)} data-testid="bid-lifecycle-actions">
      {offered.map((action, index) => (
        <Button
          key={action}
          variant={index === 0 ? "default" : "ghost"}
          size="sm"
          disabled={actions.isBusy}
          onClick={() => actions.run(action)}
        >
          {LIFECYCLE_ACTION_LABELS[action]}
        </Button>
      ))}
      {actions.confirmDialog}
    </div>
  )
}
